import { Component, HostBinding, Input, OnInit, Output, EventEmitter, SimpleChanges, OnChanges, TemplateRef, ViewChild, ElementRef, ViewContainerRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OverlayRef, Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'suka-calendar-day-picker',
  template: `
    <label *ngIf="skeleton && label" class="label skeleton"></label>
    <label *ngIf="!skeleton && label" [for]="id" class="label">
      <ng-container *ngIf="!isTemplate(label)">{{label}}</ng-container>
      <ng-template *ngIf="isTemplate(label)" [ngTemplateOutlet]="label"></ng-template>
    </label>
    <div *ngIf="!skeleton && helperText" class="form__helper-text">
      <ng-container *ngIf="!isTemplate(helperText)">{{helperText}}</ng-container>
      <ng-template *ngIf="isTemplate(helperText)" [ngTemplateOutlet]="helperText"></ng-template>
    </div>
    <div
      [attr.data-invalid]="(invalid ? true : null)"
      [attr.data-valid]="(valid ? true : null)"
      class="calendar-day-picker"
      [ngClass]="{
        'calendar-day-picker--nolabel': !label,
        'calendar-day-picker--helpertext': helperText,
        'skeleton' : skeleton,
        'calendar-day-picker--disabled': disabled
      }">
      <div class="calendar-day-picker__input-wrapper">
        <input
          #origin
          type="text"
          [id]="id"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          [required]="required"
          (focus)="openCalendarDayOverlay()"
          (change)="onInputChange($event)"/>

        <suka-icon *ngIf="!skeleton && invalid" icon="alert-circle" class="calendar-day-picker__invalid"></suka-icon>
        <suka-icon *ngIf="!skeleton && valid" icon="check" class="calendar-day-picker__valid"></suka-icon>
      </div>
      <div *ngIf="invalid" class="form-requirement">
        <ng-container *ngIf="!isTemplate(invalidText)">{{invalidText}}</ng-container>
        <ng-template *ngIf="isTemplate(invalidText)" [ngTemplateOutlet]="invalidText"></ng-template>
      </div>
    </div>

    <ng-template #calendarDayOverlay>
      <suka-calendar-day-overlay
        [days]="_selectedDays"
        (daysChange)="onDaysChange($event)"
      >
      </suka-calendar-day-overlay>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CalendarDayPicker,
      multi: true
    }
  ]
})
export class CalendarDayPicker implements ControlValueAccessor {
  /**
   * Variable used for creating unique ids for color input components.
   */
  static calendarDayPickerCount = 0;
  private _overlayRef: OverlayRef;
  private _portal: TemplatePortal<any>;

  _selectedDays: number[] = [];

  @HostBinding('class.calendar-day-picker') baseClass = true;

  @HostBinding('class.form-item') containerClass = true;
  /**
   * Set to `true` for a disabled color input.
   */
  @Input() disabled = false;
  /**
   * Set to `true` for a loading color input component.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` for an invalid color input component.
   */
  @Input() invalid = false;
  /**
   * Set to `true` for a valid color input component.
   */
  @Input() valid = false;
  /**
   * The unique id for the color input component.
   */
  @Input() id = `calendar-day-picker-${CalendarDayPicker.calendarDayPickerCount}`;
  /**
   * Reflects the required attribute of the `input` element.
   */
  @Input() required: boolean;
  /**
   * Sets the value attribute on the `input` element.
   */
  @Input() value = '';
  /**
   * Sets the placeholder on the `input` element.
   */
  @Input() placeholder = '';
  /**
   * Sets the text inside the `label` tag.
   */
  @Input() label: string | TemplateRef<any>;
  /**
   * Sets the optional helper text.
   */
  @Input() helperText: string | TemplateRef<any>;
  /**
   * Sets the invalid text.
   */
  @Input() invalidText: string | TemplateRef<any>;
  /**
   * Emits event notifying other classes when a change in state occurs in the input.
   */
  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter();

  @ViewChild('origin', { static: false }) origin: ElementRef;
  @ViewChild('calendarDayOverlay', { static: false }) dialog: TemplateRef<any>;

  /**
   * Creates an instance of `CalendarDayPicker`.
   */
  constructor(
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef
  ) {
    CalendarDayPicker.calendarDayPickerCount++;
  }

  /**
   * This is the initial value set to the component
   * @param value The input value.
   */
  public writeValue(value: any) {
    this.value = value || '';

    if (value) {
      this._selectedDays = JSON.parse('[' + value + ']');
    }
  }

  /**
   * Sets a method in order to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the number input is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state through the model
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  /**
   * Called when number input is blurred. Needed to properly implement `ControlValueAccessor`.
   */
  onTouched: () => any = () => { };

  /**
   * Method set in `registerOnChange` to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };

  /**
   * Creates a class of `NumberChange` to emit the change in the `Number`.
   */
  emitChangeEvent(): void {
    this.change.emit(this.value);
    this.propagateChange(this.value);
  }

  onInputChange(days: string) {
    this.value = JSON.parse('[' + days + ']');
    this.emitChangeEvent();
  }

  onDaysChange(days: number[]) {
    this.value = days.toString();
    this._selectedDays = days;
    this.emitChangeEvent();
  }

  /**
   * Opens the color picker dialog
   */
  openCalendarDayOverlay() {
    if (!this._overlayRef) {
      const positionStrategy = this._overlay
        .position()
        .flexibleConnectedTo(this.origin)
        .withPositions([{
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        }]);

      this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        positionStrategy,
      });

      this._overlayRef.backdropClick().subscribe(() => this._overlayRef.detach());
      this._portal = new TemplatePortal(this.dialog, this._viewContainerRef);

    }

    this._overlayRef.attach(this._portal);
  }

  /**
   * Closes the calendar day dialog
   */
  closeCalendarDayOverlay() {
    this._overlayRef.detach();
  }

  /**
   * Checks if it is a template
   */
  public isTemplate(value) {
    return value instanceof TemplateRef;
  }
}
